import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/modal'
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  Icon,
  IconButton,
  Link,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Switch,
  Text,
} from '@chakra-ui/react'
import type { FieldProps } from 'formik'
import { Field, Formik } from 'formik'
import React, { useContext, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { BsInfoCircleFill } from 'react-icons/bs'
import { MdPublishedWithChanges } from 'react-icons/md'
import useFetch, { CachePolicies } from 'use-http'
import * as Yup from 'yup'
import { colors } from '../../../../ui-kit'
import useToast from '../../../hooks/useToast'
import type {
  PublishFilesErrorResponse,
  PublishFilesSuccessResponse,
  FileDetailsResponse,
  MediaLibrarySearchFileResponse,
} from '../../../interfaces'
import { MLContext } from '../../../providers'
import { MediaLibraryActions } from '../../../providers/MLProvider'

interface Props {
  isOpen: boolean
  onClose(): void
  fileDetails?: FileDetailsResponse
}

interface FormProps {
  publish: boolean
  includeFileVersions: boolean
}

const PublishFilesModal = ({ fileDetails, isOpen, onClose }: Props): React.ReactElement => {
  const { t } = useTranslation()
  const toast = useToast()
  const [formError, setFormError] = useState<string>('')
  const { response, request } = useFetch({
    cachePolicy: CachePolicies.NO_CACHE,
  })
  let [{ selectedFiles }, dispatch] = useContext(MLContext)
  if(fileDetails){
    selectedFiles = [fileDetails as unknown as MediaLibrarySearchFileResponse]
  }
  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        onClose()
        setFormError('')
      }}
      size="lg"
    >
      <ModalOverlay />
      <ModalContent>
        <Formik<FormProps>
          enableReinitialize
          initialValues={{
            publish:
              selectedFiles.length === 1 ? selectedFiles[0].isPublished : true,
            includeFileVersions: false,
          }}
          onSubmit={async (values): Promise<void> => {
            setFormError('')
            const publishString2 = values.publish ? 'publish' : 'unpublish'
            const publishString3 = values.publish
              ? 'publishing'
              : 'unpublishing'

            let message

            const res = (await request.put('/api/v2/files/publish/', {
              fileIds: selectedFiles.map(file => file.fileId),
              publish: values.publish,
              includeFileVersions: values.includeFileVersions,
            })) as PublishFilesErrorResponse | PublishFilesSuccessResponse

            if (request.error) {
              if ([400, 403, 404].includes(response.status)) {
                setFormError((res as PublishFilesErrorResponse).message)
              } else {
                message = (
                  <Trans t={t} i18nKey="error_in_publish_file">
                    Error in {{ publishString3 }} file, please try again after
                    some time.
                  </Trans>
                )
                setFormError(message)
              }
            } else {
              if ((res as PublishFilesErrorResponse).errors) {
                message = (
                  <Trans
                    t={t}
                    i18nKey="files_published_successfully_async_with_errors"
                  >
                    Your request to {{ publishString2 }} files has been
                    submitted successfully. It would finish in some time. Some
                    files could not be processed because of path policy
                    restrictions, check response body for details.
                  </Trans>
                )
              } else {
                message = (
                  <Trans t={t} i18nKey="files_published_successfully_async">
                    Your request to {{ publishString2 }} files has been
                    submitted successfully. It would finish in some time.
                  </Trans>
                )
              }
              toast({
                description: message,
                status: 'info',
                isClosable: true,
                duration: 5000,
                position: 'top-right',
                variant: 'outline',
              })
              dispatch({
                type: MediaLibraryActions.DESELECT_ALL_FILES_AND_FOLDERS,
              })
              setFormError('')
              dispatch({
                type: MediaLibraryActions.UPDATE_FILES,
                payload: selectedFiles.map(file => ({
                  ...file,
                  isPublished: values.publish,
                })),
              })
              onClose()
            }
          }}
          validationSchema={Yup.object().shape({
            publish: Yup.boolean().required('Required'),
            includeFileVersions: Yup.boolean().required('Required'),
          })}
        >
          {({ values, handleChange, isSubmitting, submitForm }) => (
            <>
              <ModalHeader>
                <Icon
                  as={MdPublishedWithChanges}
                  color="appBattleshipGrey"
                  backgroundColor="appPaleGrey"
                  boxSize={6}
                  padding="0.1em"
                  borderRadius="full"
                  marginRight="0.5em"
                />
                {t('publish_unpublish_files', 'Publish or unpublish files')}
              </ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                <Alert status="info">
                  <AlertIcon />
                  <AlertDescription>
                    <Trans t={t} i18nKey="publish_file_alert">
                      <Text lineHeight="1.2em">
                        Unpublished files and file versions can only be accessed
                        via the media library. A purge request for each file to
                        be unpublished would be issued and would be counted
                        against the account's purge API limits.
                      </Text>{' '}
                      <Link
                        lineHeight="1.2em"
                        href="https://docs.imagekit.io/media-library/overview/draft-assets"
                        isExternal
                      >
                        Read more
                      </Link>{' '}
                      about unpublishing a file.
                    </Trans>
                  </AlertDescription>
                </Alert>
                {formError ? (
                  <Alert status="error" mt="1em">
                    <AlertIcon />
                    <AlertDescription>{formError}</AlertDescription>
                  </Alert>
                ) : null}
                <Field name="publish">
                  {({ field, form }: FieldProps<string, FormProps>) => (
                    <FormControl
                      marginTop="1em"
                      display="flex"
                      isInvalid={Boolean(
                        form.errors.publish && form.touched.publish,
                      )}
                      isRequired
                    >
                      <Switch
                        {...field}
                        id="publish"
                        size="sm"
                        isInvalid={Boolean(
                          form.errors.publish && form.touched.publish,
                        )}
                        isChecked={values.publish}
                        onChange={handleChange}
                      >
                        {t('publish', 'Publish')}
                      </Switch>
                      <Popover trigger="hover" direction="ltr">
                        <PopoverTrigger>
                          <Flex>
                            <IconButton
                              aria-label="Info icon"
                              icon={<BsInfoCircleFill />}
                              color={colors.appBlueyGrey}
                              size="sm"
                              variant="link"
                            />
                          </Flex>
                        </PopoverTrigger>
                        <PopoverContent>
                          <PopoverArrow />
                          <PopoverBody>
                            <Text>
                              {t(
                                'publish_checkbox_description',
                                'If checked, then the selected file(s) would be published, otherwise they would be unpublished.',
                              )}
                            </Text>
                          </PopoverBody>
                        </PopoverContent>
                      </Popover>
                      <FormErrorMessage mt="0.25em">
                        {form.errors.publish}
                      </FormErrorMessage>
                    </FormControl>
                  )}
                </Field>
                <Field name="includeFileVersions">
                  {({ field, form }: FieldProps<string, FormProps>) => (
                    <FormControl
                      marginTop="1em"
                      isInvalid={Boolean(
                        form.errors.includeFileVersions &&
                          form.touched.includeFileVersions,
                      )}
                      isRequired
                    >
                      <Switch
                        {...field}
                        id="includeFileVersions"
                        size="sm"
                        isInvalid={Boolean(
                          form.errors.includeFileVersions &&
                            form.touched.includeFileVersions,
                        )}
                        isChecked={values.includeFileVersions}
                        onChange={handleChange}
                      >
                        {t('include_file_versions', 'Include file versions')}
                      </Switch>
                      <FormErrorMessage mt="0.25em">
                        {form.errors.includeFileVersions}
                      </FormErrorMessage>
                    </FormControl>
                  )}
                </Field>
              </ModalBody>
              <ModalFooter
                borderTop={`1px solid ${colors.appGrey}`}
                style={{ gap: '1em' }}
              >
                <Button
                  onClick={() => {
                    onClose()
                    setFormError('')
                  }}
                  variant="outline"
                >
                  {t('cancel', 'Cancel')}
                </Button>
                <Button
                  type="submit"
                  isDisabled={isSubmitting}
                  isLoading={isSubmitting}
                  onClick={async () => {
                    await submitForm()
                  }}
                >
                  {t('submit', 'Submit')}
                </Button>
              </ModalFooter>
            </>
          )}
        </Formik>
      </ModalContent>
    </Modal>
  )
}

export default PublishFilesModal
